"use client";

import { IProduct } from "@/types/product-d-t";
import ProductItem from "../products/single-product/product-item";
import { useState, useEffect } from "react";

type IProps = {
  allProducts?: IProduct[];
  categoryName?: string;
};

const ShopArea = ({ allProducts = [], categoryName }: IProps) => {
  const INITIAL_LOAD = 15;
  const LOAD_MORE = 10;

  const [visibleCount, setVisibleCount] = useState<number>(INITIAL_LOAD);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSeeMore = () => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE, allProducts.length));
  };

  const productsToShow = allProducts.slice(0, visibleCount);
  const productCount = allProducts.length;

  const getFlexBasis = () => {
    if (windowWidth < 480) return "100%";
    if (windowWidth < 768) return "calc(50% - 12px)";
    if (windowWidth < 1024) return "calc(33.33% - 16px)";
    return "calc(20% - 20px)";
  };

  return (
    <section style={{ padding: windowWidth < 768 ? "30px 16px" : "50px 0", textAlign: "center" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {allProducts.length === 0 ? (
          <div style={{ marginTop: "50px" }}>
            <div style={{ fontSize: windowWidth < 768 ? "14px" : "16px", color: "#555" }}>
              Loading products
              <span className="dots">...</span>
            </div>

            {/* Animation for dots */}
            <style jsx>{`
              .dots::after {
                content: '';
                display: inline-block;
                width: 0.8em;
                text-align: left;
                animation: dots 1s steps(5, end) infinite;
              }

              @keyframes dots {
                0%, 20% { content: ''; }
                40% { content: '.'; }
                60% { content: '..'; }
                80%, 100% { content: '...'; }
              }
            `}</style>
          </div>
        ) : (
          <>
            {/* Category Title */}
            <h2
              style={{
                fontSize: windowWidth < 480 ? "22px" : windowWidth < 768 ? "28px" : "36px",
                fontWeight: 900,
                marginBottom: windowWidth < 768 ? "20px" : "30px",
                color: "#222",
                lineHeight: 1.2,
              }}
            >
              {categoryName ? `Explore ${categoryName} Collection` : "Discover Our Products"}{" "}
              <span
                style={{
                  color: "#000000ff",
                  animation: "pulse 1.5s infinite",
                }}
              >
                ({productCount})
              </span>{" "}
              ✨
            </h2>

            {/* Highlight product count animation */}
            <style jsx>{`
              @keyframes pulse {
                0% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.2); opacity: 0.8; }
                100% { transform: scale(1); opacity: 1; }
              }
            `}</style>

            <p
              style={{
                fontSize: windowWidth < 768 ? "13px" : "16px",
                color: "#555",
                marginBottom: windowWidth < 768 ? "25px" : "40px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {productCount > 0
                ? `We have ${productCount} ${
                    productCount === 1 ? "item" : "items"
                  } in ${categoryName || "all categories"}. Browse our latest collection and find your perfect style!`
                : "No products found in this category."}
            </p>

            <br></br>

            {/* Product Grid */}
            {productCount > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: windowWidth < 768 ? "12px" : "20px",
                }}
              >
                {productsToShow.map((product: any) => (
                  <div
                    key={product.id}
                    style={{
                      flex: `0 0 ${getFlexBasis()}`,
                      maxWidth: getFlexBasis(),
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <ProductItem product={product} />
                  </div>
                ))}
              </div>
            )}

            {/* See More Button */}
            {visibleCount < allProducts.length && (
              <div style={{ marginTop: "30px", display: "flex", justifyContent: "center" }}>
                <button
                  onClick={handleSeeMore}
                  style={{
                    padding: windowWidth < 480 ? "10px 20px" : "12px 28px",
                    minWidth: "150px",
                    borderRadius: "8px",
                    background: "#000",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: windowWidth < 480 ? "13px" : "14px",
                    letterSpacing: "1px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#333")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#000")}
                >
                  See More ({productCount})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ShopArea;
